// VerifyInvitation (NEW DESIGN — visual revamp only). Logic verbatim copy of
// VerifyInvitation.js. Differences:
//   - LoginNewWrapper instead of LoginWrapper (imported from sibling Login folder).
//   - Set-password redirect and "Go to Login" CTA use canonical /auth/np2w8q and /login.
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Row, Col, Result } from 'antd';
import LottieLoader from '../../Components/common/LottieLoader';
import { verifyInvitation } from '../../services/invitation.service';
import { STORAGE_KEYS } from '../../utils/constants';
import { LoginNewWrapper } from '../Login/LoginNewStyle';
import LoginLeftPanel from '../Login/LoginLeftPanel';
import { ThemeButton } from '../Styles/Theme';
import { setPermissions } from '../../store/actions/permissionsAction';
import { checkModulePermissions, getFirstAvailableRoute } from '../../utils/helpers';

const VerifyInvitationNew = () => {
	const { token } = useParams();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [status, setStatus] = useState('loading');
	const [message, setMessage] = useState('');
	const [errorCode, setErrorCode] = useState(null);

	useEffect(() => {
		if (!token) {
			setStatus('error');
			setMessage('Invalid invitation link. Please check your email for the correct link.');
			setErrorCode('INVALID_TOKEN');
			return;
		}

		const verify = async () => {
			const result = await verifyInvitation(token);

			if (result.success) {
				const data = result.data || {};

				if (data.mode === 'logged-in' && data.accessToken) {
					localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, data.accessToken);
					localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, data.refreshToken);
					localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(data.user));
					if (data.permissions) {
						localStorage.setItem(STORAGE_KEYS.PERMISSIONS, JSON.stringify(data.permissions));
						dispatch(setPermissions(data.permissions));
					}
					if (data.appVersion) {
						localStorage.setItem('app_version', data.appVersion);
					}
					// Per-org login redirect: backend sets redirect_chat_id when the
					// user's org has chat_redirection_enabled. Take that path before
					// the normal landing-page fallback so the user lands directly in
					// their fresh "New Insights" chat.
					if (data?.redirect_chat_id) {
						navigate(`/insight-engine/${data.redirect_chat_id}`);
						return;
					}
					const dashboardPermission = checkModulePermissions('dashboard');
					if (dashboardPermission.authorized) {
						navigate('/', { replace: true });
					} else {
						const first = getFirstAvailableRoute();
						navigate(first ? first.path : '/', { replace: true });
					}
					return;
				}

				localStorage.setItem(STORAGE_KEYS.SESSION_TOKEN, data.sessionToken);
				localStorage.setItem(STORAGE_KEYS.PENDING_USER, JSON.stringify(data.user));
				navigate('/auth/np2w8q', { replace: true });
			} else {
				setStatus('error');
				setMessage(result.message);
				setErrorCode(result.data?.code || 'UNKNOWN_ERROR');
			}
		};

		verify();
	}, [token, navigate, dispatch]);

	const getErrorTitle = () => {
		switch (errorCode) {
			case 'TOKEN_EXPIRED':
				return 'Link Expired';
			case 'ALREADY_ACTIVE':
				return 'Account Already Active';
			case 'ACCOUNT_SUSPENDED':
				return 'Account Suspended';
			case 'INVALID_TOKEN':
				return 'Invalid Invitation Link';
			default:
				return 'Verification Failed';
		}
	};

	const getErrorMessage = () => {
		switch (errorCode) {
			case 'TOKEN_EXPIRED':
				return 'This invitation link has expired. Please contact your administrator to receive a new invitation.';
			case 'ALREADY_ACTIVE':
				return 'Your account is already activated. Please proceed to sign in.';
			case 'ACCOUNT_SUSPENDED':
				return 'Your account has been suspended. Please contact your administrator for assistance.';
			case 'INVALID_TOKEN':
				return 'This invitation link is invalid or has already been used. Please contact your administrator to receive a new invitation.';
			default:
				return message || 'Something went wrong. Please contact your administrator for assistance.';
		}
	};

	const getErrorStatus = () => {
		if (errorCode === 'TOKEN_EXPIRED') return 'warning';
		if (errorCode === 'ALREADY_ACTIVE') return 'info';
		return 'error';
	};

	if (status === 'loading') {
		return (
			<LoginNewWrapper>
				<Row gutter={[20, 16]}>
					<Col
						xs={{ span: 24, order: 1 }}
						sm={{ span: 24, order: 1 }}
						md={{ span: 12, order: 2 }}
						lg={{ span: 14, order: 2 }}
						xl={{ span: 14, order: 2 }}
					>
						<div className='loginRightCard'>
							<div className='loginLogo'>
								<img src='/images/login-new/crowdvox-logo.svg' alt='CrowdVox' />
							</div>

							<div className='loginContainer'>
								<div className='loginStatusBlock'>
									<LottieLoader inline size='medium' />
									<h1>Verifying Invitation...</h1>
									<p>Please wait while we verify your invitation link.</p>
								</div>
							</div>

							<div className='loginFooter'>
								<span>@{new Date().getFullYear()} CrowdVox Inc.</span>
							</div>
						</div>
					</Col>

					<Col
						xs={{ span: 0, order: 2 }}
						sm={{ span: 0, order: 2 }}
						md={{ span: 12, order: 1 }}
						lg={{ span: 10, order: 1 }}
						xl={{ span: 10, order: 1 }}
					>
						<LoginLeftPanel />
					</Col>
				</Row>
			</LoginNewWrapper>
		);
	}

	return (
		<div style={{
			display: 'flex',
			justifyContent: 'center',
			alignItems: 'center',
			minHeight: '100vh',
			padding: '20px',
			backgroundColor: '#f5f5f5',
		}}>
			<Result
				status={getErrorStatus()}
				title={getErrorTitle()}
				subTitle={getErrorMessage()}
				extra={
					<ThemeButton size='large' className='loginBtn' onClick={() => navigate('/login', { replace: true })}>
						Go to Login
					</ThemeButton>
				}
			/>
		</div>
	);
};

export default VerifyInvitationNew;
